import { EventDispatcher, Vector3, /*  Euler, */ Object3D, Camera /* , Quaternion */, Euler } from 'three';
import { DeviceOrientationControls } from 'three/examples/jsm/controls/DeviceOrientationControls';
import lerp from 'lerp';
import { CustomObject } from '../../types/data';
import * as device from '../device';
import { Confirm } from '../attention';

// To avoid recalculation at every mouse movement tick
let PI_2 = Math.PI / 2;

class LookControls extends EventDispatcher {
  public object3D: Object3D;
  public pitchObject: Object3D;
  public yawObject: Object3D;

  private domElement: HTMLElement;
  private deltaMovement?: {
    movementX: number;
    movementY: number;
  };
  private inertiaTarget?: Vector3;
  private magicWindowAbsoluteEuler: Euler;
  private magicWindowDeltaEuler: Euler;
  private magicWindowObject: Object3D;
  private magicWindowControls?: DeviceOrientationControls;
  public data: {
    enabled: boolean;
    magicWindowTrackingEnabled: boolean;
    touchEnabled: boolean;
    mouseEnabled: boolean;

    reverseMouseDrag: boolean;
    reverseTouchDrag: boolean;
  };
  private previousMouseEvent: CustomObject;
  private previousMagicWindowYaw?: number;
  private mouseDown: boolean;
  private touchStarted?: boolean;
  private touchStart: {
    x: number;
    y: number;
  };

  public constructor(object3D: Object3D, domElement: HTMLElement) {
    super();

    this.data = {
      enabled: true,
      magicWindowTrackingEnabled: true,
      touchEnabled: true,
      mouseEnabled: true,

      reverseMouseDrag: true,
      reverseTouchDrag: true,
    };

    this.object3D = object3D;
    this.domElement = domElement;

    // this.deltaYaw = 0;
    // this.previousHMDPosition = new Vector3();
    // this.hmdQuaternion = new Quaternion();
    this.magicWindowAbsoluteEuler = new Euler();
    this.magicWindowDeltaEuler = new Euler();
    // this.position = new Vector3();
    this.magicWindowObject = new Object3D();
    // this.rotation = {};
    // this.deltaRotation = {};
    this.previousMouseEvent = {};
    this.touchStart = {
      x: 0,
      y: 0,
    };
    // To save / restore camera pose
    // this.savedPose = {
    //   position: new Vector3(),
    //   rotation: new Euler(),
    // };

    this.mouseDown = false;
    this.pitchObject = new Object3D();
    this.yawObject = new Object3D();
    this.yawObject.position.y = 10;
    this.yawObject.add(this.pitchObject);

    this.setupMagicWindowControls();

    this.addEventListeners();

    // Call enter VR handler if the scene has entered VR before the event listeners attached.
    // if (this.el.sceneEl.is('vr-mode')) {
    //   this.onEnterVR();
    // }
  }

  private setupMagicWindowControls() {
    // Only on mobile devices and only enabled if DeviceOrientation permission has been granted.
    if (device.isMobile()) {
      this.magicWindowControls = new DeviceOrientationControls(this.magicWindowObject as Camera);
      this.magicWindowControls.enabled = false;
      if (typeof DeviceOrientationEvent !== 'undefined' && DeviceOrientationEvent.requestPermission) {
        // iOS 13+

        new Confirm({
          title: 'Orientation permission',
          content: 'Giving this permission you can control the view by moving the phone.',
          onConfirm: () => {
            window.DeviceOrientationEvent.requestPermission()
              .then(response => {
                if (response === 'granted') {
                  if (this.magicWindowControls) {
                    this.magicWindowControls.enabled = true;
                  }
                }
              })
              .catch(() => {
                console.log('permission denied');
              });
          },
        });
      } else {
        this.magicWindowControls.enabled = true;
      }
    }
  }

  /**
   * Update orientation for mobile, mouse drag, and headset.
   * Mouse-drag only enabled if HMD is not active.
   */
  private updateOrientation = () => {
    if ((!this.magicWindowControls?.enabled || !this.data.magicWindowTrackingEnabled) && this.inertiaTarget) {
      this.pitchObject.rotation.x = Math.max(
        -PI_2,
        Math.min(PI_2, lerp(this.object3D.rotation.x, this.inertiaTarget.x, 0.2))
      );
      this.yawObject.rotation.y = lerp(this.object3D.rotation.y, this.inertiaTarget.y, 0.2);
    }

    this.updateMagicWindowOrientation();

    const newX = this.magicWindowDeltaEuler.x + this.pitchObject.rotation.x;
    const newY = this.magicWindowDeltaEuler.y + this.yawObject.rotation.y;
    const newZ = 0; // this.magicWindowDeltaEuler.z;

    if (this.object3D.rotation.x !== newX || this.object3D.rotation.y !== newY) {
      this.object3D.rotation.set(newX, newY, newZ, 'YXZ');
    } else {
      delete this.inertiaTarget;
    }
  };

  private updateMagicWindowOrientation = () => {
    // Calculate magic window HMD quaternion.
    if (this.data.magicWindowTrackingEnabled && this.magicWindowControls && this.magicWindowControls.enabled) {
      this.magicWindowControls.update();
      this.magicWindowAbsoluteEuler.setFromQuaternion(this.magicWindowObject.quaternion, 'YXZ');
      if (!this.previousMagicWindowYaw && this.magicWindowAbsoluteEuler.y !== 0) {
        this.previousMagicWindowYaw = this.magicWindowAbsoluteEuler.y;
      }
      if (this.previousMagicWindowYaw) {
        this.magicWindowDeltaEuler.x = this.magicWindowAbsoluteEuler.x;
        this.magicWindowDeltaEuler.y += this.magicWindowAbsoluteEuler.y - this.previousMagicWindowYaw;
        this.magicWindowDeltaEuler.z = this.magicWindowAbsoluteEuler.z;
        this.previousMagicWindowYaw = this.magicWindowAbsoluteEuler.y;
      }
    } else {
      this.magicWindowDeltaEuler.x = 0;
      this.magicWindowDeltaEuler.y = 0;
      this.magicWindowDeltaEuler.z = 0;
    }
  };

  /**
   * Add mouse and touch event listeners to canvas.
   */
  private addEventListeners() {
    // let sceneEl = this.el.sceneEl;
    const canvasEl = this.domElement;

    if (!canvasEl) return;

    // Wait for canvas to load.
    // if (!canvasEl) {
    //   sceneEl.addEventListener('render-target-loaded', bind(this.addEventListeners, this));
    //   return;
    // }

    // Mouse events.
    canvasEl.addEventListener('mousedown', this.onMouseDown, false);
    window.addEventListener('mousemove', this.onMouseMove, false);
    window.addEventListener('mouseup', this.onMouseUp, false);

    // Touch events.
    canvasEl.addEventListener('touchstart', this.onTouchStart);
    window.addEventListener('touchmove', this.onTouchMove);
    window.addEventListener('touchend', this.onTouchEnd);

    // sceneEl events.
    // sceneEl.addEventListener('enter-vr', this.onEnterVR);
    // sceneEl.addEventListener('exit-vr', this.onExitVR);
  }

  /**
   * Remove mouse and touch event listeners from canvas.
   */
  private removeEventListeners() {
    // let sceneEl = this.el.sceneEl;
    const canvasEl = this.domElement;

    if (!canvasEl) {
      return;
    }

    // Mouse events.
    canvasEl.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseup', this.onMouseUp);

    // Touch events.
    canvasEl.removeEventListener('touchstart', this.onTouchStart);
    window.removeEventListener('touchmove', this.onTouchMove);
    window.removeEventListener('touchend', this.onTouchEnd);

    // sceneEl events.
    // sceneEl.removeEventListener('enter-vr', this.onEnterVR);
    // sceneEl.removeEventListener('exit-vr', this.onExitVR);
  }

  private moveWithInertia = () => {
    if (!this.deltaMovement) {
      return;
    }

    const { pitchObject, yawObject } = this;
    const direction = device.isMobile() ? (this.data.reverseTouchDrag ? -1 : 1) : this.data.reverseMouseDrag ? 1 : -1;

    const { movementX, movementY } = this.deltaMovement;

    if (!movementX && !movementY) {
      return;
    }

    this.inertiaTarget = new Vector3(
      pitchObject.rotation.x + movementY * 0.006 * direction,
      yawObject.rotation.y + movementX * 0.006 * direction,
      0
    );

    this.deltaMovement = {
      movementX: 0,
      movementY: 0,
    };
  };

  /**
   * Register mouse down to detect mouse drag.
   */
  private onMouseDown = (evt: MouseEvent) => {
    // Handle only primary button.
    if (!this.data.enabled || !this.data.mouseEnabled || evt.button !== 0) {
      return;
    }

    if (this.inertiaTarget) {
      delete this.inertiaTarget;
    }

    this.mouseDown = true;
    this.previousMouseEvent = evt;
  };

  /**
   * Translate mouse drag into rotation.
   *
   * Dragging up and down rotates the camera around the X-axis (yaw).
   * Dragging left and right rotates the camera around the Y-axis (pitch).
   */
  private onMouseMove = (event: MouseEvent) => {
    let movementX;
    let movementY;
    const { pitchObject, previousMouseEvent, yawObject } = this;

    // Not dragging or not enabled.
    if (!this.data.enabled || !this.data.mouseEnabled || !this.mouseDown) {
      return;
    }

    // Calculate delta.
    movementX = event.screenX - previousMouseEvent.screenX;
    movementY = event.screenY - previousMouseEvent.screenY;

    this.previousMouseEvent = event;

    this.deltaMovement = {
      movementX,
      movementY,
    };

    // Calculate rotation.
    const direction = this.data.reverseMouseDrag ? 1 : -1;
    yawObject.rotation.y += movementX * 0.002 * direction;
    pitchObject.rotation.x += movementY * 0.002 * direction;
    pitchObject.rotation.x = Math.max(-PI_2, Math.min(PI_2, pitchObject.rotation.x));
  };

  /**
   * Register mouse up to detect release of mouse drag.
   */
  private onMouseUp = () => {
    this.mouseDown = false;
    this.moveWithInertia();
  };

  /**
   * Register touch down to detect touch drag.
   */
  private onTouchStart = (evt: TouchEvent) => {
    if (evt.touches.length !== 1 || !this.data.touchEnabled /* || this.el.sceneEl.is('vr-mode') */) {
      return;
    }
    // evt.preventDefault();
    // evt.stopPropagation();

    if (this.inertiaTarget) {
      delete this.inertiaTarget;
    }

    this.touchStart = {
      x: evt.touches[0].pageX,
      y: evt.touches[0].pageY,
    };
    this.touchStarted = true;
  };

  /**
   * Translate touch move to Y-axis rotation.
   */
  private onTouchMove = (evt: TouchEvent) => {
    if (!this.touchStarted || !this.data.touchEnabled) {
      return;
    }
    // evt.preventDefault();
    // evt.stopPropagation();

    const canvas = this.domElement;
    const pitchObject = this.pitchObject;
    const yawObject = this.yawObject;

    const deltaY = (2 * Math.PI * (evt.touches[0].pageX - this.touchStart.x)) / canvas.clientWidth;
    const deltaX = (2 * Math.PI * (evt.touches[0].pageY - this.touchStart.y)) / canvas.clientHeight;

    this.deltaMovement = {
      movementX: deltaX,
      movementY: deltaY,
    };

    const direction = this.data.reverseTouchDrag ? -1 : 1;
    // Limit touch orientaion to to yaw (y axis).
    yawObject.rotation.y -= deltaY * 0.5 * direction;

    pitchObject.rotation.x -= deltaX * 0.5 * direction;
    pitchObject.rotation.x = Math.max(-PI_2, Math.min(PI_2, pitchObject.rotation.x));

    this.touchStart = {
      x: evt.touches[0].pageX,
      y: evt.touches[0].pageY,
    };
  };

  /**
   * Register touch end to detect release of touch drag.
   */
  private onTouchEnd = () => {
    this.touchStarted = false;
    this.moveWithInertia();
  };

  public tick = () => {
    let data = this.data;
    if (!data.enabled) {
      return;
    }
    this.updateOrientation();
  };

  public remove = () => {
    this.removeEventListeners();
  };

  // rotation in radians
  public lookAt = (rotation: Euler) => {
    if (!rotation) return;
    const { x, y } = rotation;

    delete this.inertiaTarget;

    this.pitchObject.rotation.x = x;

    this.yawObject.rotation.y = y;
  };

  // rotation in radians
  public lookAtWithAnimation = (rotation: Euler) => {
    const { x, y } = rotation;

    this.inertiaTarget = new Vector3(x, y, 0);
  };
}

export default LookControls;
